import React, { useCallback, useEffect } from 'react';
import './TeacherDetails.css';
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Collapse,
} from '@mui/material';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import Layout from '../../../../../components/Layout';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState } from 'react';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import PropTypes from 'prop-types';
import SingleTeacherDetails from '../../../../../components/Admin/UserManagement/Teacher/TeachersDetails/SingleTeacherDetails';
import TeacherSession from '../../../../../components/Admin/UserManagement/Teacher/TeacherSession/TeacherSession';
import TeacherEnableModal from '../../../../../components/Admin/UserManagement/Teacher/TeacherEnableModal/TeacherEnableModal';
import TeacherEarnings from '../../../../../components/Admin/UserManagement/Teacher/TeacherEarnings/TeacherEarnings';
import TeacherOverview from '../../../../../components/Admin/UserManagement/Teacher/TeacherOverview/TeacherOverview';
import { toast } from 'react-hot-toast';

const StatusColor = {
  approval_pending: { color: '#EBBF37', bgcolor: '#FFFAEC', value: 'Pending' },
  approved: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Approved' },
  rejected: { color: '#E0AD14', bgcolor: '#FFFBF0', value: 'Rejected' },
  enable: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Enabled' },
  disable: { color: '#949DA6', bgcolor: '#F5F5F5', value: 'Disabled' },
};

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const TeacherDetails = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [permissionsExpanded, setPermissionsExpanded] = useState(false);
  const [rowId, setRowId] = useState(null);
  const [teacherName, setTeacherName] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [teacherDetails, setTeacherDetails] = useState([]);
  const [bankDetails, setBankDetails] = useState([]);
  const [commentList, setCommentList] = useState([]);
  const [canUseWallet, setCanUseWallet] = useState(true);
  const [canUseChat, setCanUseChat] = useState(true);
  const [canUseBookings, setCanUseBookings] = useState(true);

  // Temporary permissions state for the modal
  const [tempWalletAccess, setTempWalletAccess] = useState(true);
  const [tempChatAccess, setTempChatAccess] = useState(true);
  const [tempBookingAccess, setTempBookingAccess] = useState(true);

  const [featureToToggle, setFeatureToToggle] = useState(null);
  const [isEnabling, setIsEnabling] = useState(false);
  const [openPermissionsModal, setOpenPermissionsModal] = useState(false);
  const [openRemoveFromOrgModal, setOpenRemoveFromOrgModal] = useState(false);
  const [isRemovingFromOrg, setIsRemovingFromOrg] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // RBAC permissions
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [canManageOrganisations, setCanManageOrganisations] = useState(false);
  const [isSupportAssociate, setIsSupportAssociate] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        const perms = Array.isArray(data?.permissions) ? data.permissions : [];
        const roles = Array.isArray(data?.roles) ? data.roles : [];
        if (mounted) {
          setCanManageUsers(perms.includes('manage_users'));
          setCanManageOrganisations(perms.includes('manage_organisations'));
          setIsSupportAssociate(roles.includes('support_associate'));
        }
      } catch (_) {
        if (mounted) {
          setCanManageUsers(false);
          setCanManageOrganisations(false);
          setIsSupportAssociate(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Remove URL-based org_id logic since we'll get it from API response
  // const searchParams = new URLSearchParams(location.search);
  // const urlOrgId = searchParams.get('org_id');

  // Get the initial tab value from URL search params or session storage
  const getInitialTabValue = () => {
    // First check URL search params
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    if (tabParam !== null) {
      const tabValue = parseInt(tabParam, 10);
      return isNaN(tabValue) ? 0 : tabValue;
    }

    // Then check session storage
    const savedTab = sessionStorage.getItem(`teacherTab-${id}`);
    if (savedTab !== null) {
      const tabValue = parseInt(savedTab, 10);
      return isNaN(tabValue) ? 0 : tabValue;
    }

    // Default to 0 (Overview)
    return 0;
  };

  const [value, setValue] = useState(getInitialTabValue());

  const handleChange = (event, newValue) => {
    setValue(newValue);
    // Save the current tab to session storage for persistence
    sessionStorage.setItem(`teacherTab-${id}`, newValue.toString());
    // Also update URL without navigating
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', newValue.toString());
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const getSingleTeachersDetails = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/admin/teachers/${id}`);
      setTeacherDetails(data);
      setCanUseWallet(data.can_use_wallet);
      setCanUseChat(data.can_use_chat);
      setCanUseBookings(data.can_use_bookings);
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  const getSingleTeachersBankDetails = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/admin/bank_details/${id}`);
      setBankDetails(data);
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  const getTeacherAllCommentDetails = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/admin/teachers/${id}/comments`);
      setCommentList(data);
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      getSingleTeachersDetails();
      getSingleTeachersBankDetails();
      getTeacherAllCommentDetails();
    }
  }, [getSingleTeachersBankDetails, getSingleTeachersDetails, getTeacherAllCommentDetails, id]);

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
    // Reset permissions expanded state when opening the menu
    setPermissionsExpanded(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setPermissionsExpanded(false);
  };

  const handleTogglePermissions = event => {
    event.stopPropagation();
    setPermissionsExpanded(!permissionsExpanded);
  };

  const handleOpenPermissionsModal = () => {
    // Initialize temp states with current values
    setTempWalletAccess(canUseWallet);
    setTempChatAccess(canUseChat);
    setTempBookingAccess(canUseBookings);
    setOpenPermissionsModal(true);
    handleMenuClose();
  };

  const handleClosePermissionsModal = () => {
    setOpenPermissionsModal(false);
  };

  const handleOpenRemoveFromOrgModal = () => {
    if (!canManageOrganisations) {
      toast.error("You don't have permission to remove instructors from an organization");
      handleMenuClose();
      return;
    }
    setOpenRemoveFromOrgModal(true);
    handleMenuClose();
  };

  const handleCloseRemoveFromOrgModal = () => {
    setOpenRemoveFromOrgModal(false);
  };

  const handleRemoveFromOrganization = async () => {
    if (!canManageOrganisations) {
      toast.error("You don't have permission to remove instructors from an organization");
      return;
    }
    try {
      setIsRemovingFromOrg(true);
      const orgId = teacherDetails?.org_id;

      if (!orgId) {
        toast.error('Organization ID not found');
        return;
      }

      await axiosInstance.delete(`/admin/organisations/${orgId}/teachers/${id}`);

      // Show success message
      toast.success('Instructor removed from organization successfully');

      // Close the modal
      handleCloseRemoveFromOrgModal();

      // Navigate back to the organisation instructor list
      // Check if org_id was in URL params (indicating we came from organisation page)
      const searchParams = new URLSearchParams(location.search);
      const urlOrgId = searchParams.get('org_id');

      if (urlOrgId || orgId) {
        // Navigate back to organisation detail page with Instructors tab (tab index 1)
        navigate(`/admin/organisation/${orgId}?tab=1`, { replace: true });
      } else {
        // If no org_id in URL, just refresh teacher details and remove org_id from URL
        await getSingleTeachersDetails();
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('org_id');
        navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
      }
    } catch (error) {
      console.error('Error removing teacher from organization:', error);
      toast.error('Failed to remove instructor from organization');
    } finally {
      setIsRemovingFromOrg(false);
    }
  };

  const handleSavePermissions = async () => {
    try {
      /*
       * The backend expects permission updates through the unified
       * /admin/teachers/action/:action/:teacher_id endpoint.
       * Determine the appropriate action based on the desired state:
       *  - If at least one of the permissions is being enabled (true), we
       *    treat this as an "enable" action.
       *  - Otherwise, treat it as a "disable" action.
       */
      const action = tempWalletAccess || tempChatAccess || tempBookingAccess ? 'enable' : 'disable';

      await axiosInstance.post(`/admin/teachers/action/${action}/${id}`, {
        can_use_wallet: tempWalletAccess,
        can_use_chat: tempChatAccess,
        can_use_bookings: tempBookingAccess,
      });

      // Update local state to reflect the change
      setCanUseWallet(tempWalletAccess);
      setCanUseChat(tempChatAccess);
      setCanUseBookings(tempBookingAccess);

      handleClosePermissionsModal();
    } catch (error) {
      console.error('Error updating permissions:', error);
    }
  };

  const handleAction = action => {
    if (action === 'disable' || action === 'enable') {
      if (!canManageUsers) {
        toast.error("You don't have permission to enable/disable users");
        handleMenuClose();
        return;
      }
      // Reset any previously selected feature toggle when performing an account level action
      setFeatureToToggle(null);
      // Track whether we are enabling or disabling the account (helps downstream logic)
      setIsEnabling(action === 'enable');

      setOpenApproveModal(true);
      setRowId(teacherDetails?.id);
      setTeacherName(`${teacherDetails?.first_name} ${teacherDetails?.last_name}`);
      setIsActive(teacherDetails?.is_active);
    }
    handleMenuClose();
  };

  const handlePasswordReset = async () => {
    const email = teacherDetails?.email;

    if (!email) {
      toast.error('Email address not found for this instructor');
      handleMenuClose();
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await axiosInstance.post('/auth/forgot-password', {
        email: email,
      });

      if (response.status === 200) {
        toast.success(`Password reset email sent to ${email}`);
        handleMenuClose();
      }
    } catch (error) {
      console.error('Password reset error:', error);
      let errorMessage = 'Failed to send password reset email';

      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail[0]?.msg || errorMessage;
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleFeatureToggle = (feature, currentValue) => {
    setFeatureToToggle(feature);
    setIsEnabling(!currentValue);
    setRowId(teacherDetails?.id);
    setTeacherName(`${teacherDetails?.first_name} ${teacherDetails?.last_name}`);
    setOpenApproveModal(true);
    handleMenuClose();
  };

  const navigateToTeacherList = () => {
    navigate('/admin/users/instructor');
  };

  return (
    <Layout title="Klass Online | Instructor Details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Box className="flex justify-between items-center">
              <Box className="flex flex-col">
                <Box className="flex items-center gap-2">
                  <Typography
                    className="user-management-request-text"
                    onClick={navigateToTeacherList}
                    style={{ cursor: 'pointer' }}
                  >
                    User Management {'>'}
                    {'>'} Instructor {'>'}
                    {'>'}
                  </Typography>
                  <Typography className="profile-text">Profile</Typography>
                </Box>
                <Box className="flex items-center gap-4">
                  <Typography className="teacher-request-name-text">
                    {teacherDetails?.first_name} {teacherDetails?.last_name}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: '16px',
                      mt: 1,
                      borderColor: '#D5DEE3',
                    }}
                  />
                  <Box
                    sx={{
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '4px 8px 4px 8px',
                      borderRadius: '20px',
                      backgroundColor: teacherDetails?.is_active
                        ? StatusColor['enable']?.bgcolor
                        : StatusColor['disable']?.bgcolor,
                      color: teacherDetails?.is_active
                        ? StatusColor['enable']?.color
                        : StatusColor['disable']?.color,
                      border: `1px solid ${teacherDetails?.is_active ? StatusColor['enable']?.color : StatusColor['disable']?.color}`,
                      fontFamily: 'Poppins !important',
                      fontSize: '10px !important',
                      fontStyle: 'normal !important',
                      fontWeight: '400 !important',
                      lineHeight: 'normal !important',
                    }}
                  >
                    <Typography
                      className="w-[6px] h-[6px] rounded-full"
                      style={{
                        backgroundColor: teacherDetails?.is_active
                          ? StatusColor['enable']?.color
                          : StatusColor['disable']?.color,
                      }}
                    ></Typography>
                    <Typography className="status-text">
                      {teacherDetails?.is_active === true ? 'Enabled' : 'Disabled'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box className="flex items-center gap-4">
                <IconButton data-testid="more-options-button" onClick={e => handleMenuClick(e)}>
                  <MoreHorizOutlinedIcon />
                </IconButton>
              </Box>
            </Box>
            <Box className="flex flex-col items-start gap-[19px] bg-[#FFF] rounded-[8px]">
              <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                    <Tab
                      label="Overview"
                      aria-label="overview tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(0)}
                    />
                    <Tab
                      label="Sessions"
                      aria-label="sessions tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(1)}
                    />
                    <Tab label="Earning" sx={{ textTransform: 'none' }} {...a11yProps(2)} />
                    <Tab label="Details" sx={{ textTransform: 'none' }} {...a11yProps(3)} />
                  </Tabs>
                </Box>
                <CustomTabPanel value={value} index={0}>
                  <TeacherOverview />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={1}>
                  <TeacherSession />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={2}>
                  <TeacherEarnings teacherId={teacherDetails?.id} />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={3}>
                  <SingleTeacherDetails
                    teacherDetails={teacherDetails}
                    bankDetails={bankDetails}
                    commentList={commentList}
                  />
                </CustomTabPanel>
              </Box>
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                width: '250px',
                padding: '4px 0',
                boxShadow: '0px 6px 10px rgba(10, 12, 18, 0.09)',
                border: '1px solid #D5DEE3',
                borderRadius: '8px',
              },
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <MenuItem
              onClick={() => handleAction(teacherDetails?.is_active ? 'disable' : 'enable')}
              className="menu-item"
            >
              <Box sx={{ pl: '4px', pr: '4px', display: 'flex', alignItems: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: '#14171F',
                  }}
                >
                  {teacherDetails?.is_active ? 'Disable' : 'Enable'}
                </Typography>
              </Box>
            </MenuItem>

            {isSupportAssociate && (
              <>
                <Divider sx={{ my: 1, borderColor: '#D5DEE3' }} />
                <MenuItem
                  onClick={handlePasswordReset}
                  className="menu-item"
                  disabled={isResettingPassword}
                >
                  <Box sx={{ pl: '4px', pr: '4px', display: 'flex', alignItems: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        color: '#14171F',
                        opacity: isResettingPassword ? 0.6 : 1,
                      }}
                    >
                      {isResettingPassword ? 'Sending...' : 'Password Reset'}
                    </Typography>
                  </Box>
                </MenuItem>
              </>
            )}

            {/* <Divider sx={{ my: 1, borderColor: '#D5DEE3' }} /> */}

            {teacherDetails?.org_id && (
              <>
                <Divider sx={{ my: 1, borderColor: '#D5DEE3' }} />
                <MenuItem onClick={handleOpenRemoveFromOrgModal} className="menu-item">
                  <Box sx={{ pl: '4px', pr: '4px', display: 'flex', alignItems: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        color: '#E53E3E',
                      }}
                    >
                      Remove from organization
                    </Typography>
                  </Box>
                </MenuItem>
              </>
            )}
          </Menu>

          {/* Edit Permissions Modal */}
          <Dialog
            open={openPermissionsModal}
            onClose={handleClosePermissionsModal}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '8px',
                padding: '8px 0',
              },
            }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontFamily: 'Poppins',
                fontWeight: '600',
                fontSize: '18px',
                borderBottom: '1px solid #eee',
                pb: 2,
              }}
            >
              Edit Permissions
              <IconButton onClick={handleClosePermissionsModal} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempWalletAccess}
                      onChange={e => setTempWalletAccess(e.target.checked)}
                      sx={{
                        color: '#0a5ef3',
                        '&.Mui-checked': {
                          color: '#0a5ef3',
                        },
                      }}
                    />
                  }
                  label="Wallet Access"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: 'Poppins',
                      fontSize: '16px',
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempChatAccess}
                      onChange={e => setTempChatAccess(e.target.checked)}
                      sx={{
                        color: '#0a5ef3',
                        '&.Mui-checked': {
                          color: '#0a5ef3',
                        },
                      }}
                    />
                  }
                  label="Chat Access"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: 'Poppins',
                      fontSize: '16px',
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempBookingAccess}
                      onChange={e => setTempBookingAccess(e.target.checked)}
                      sx={{
                        color: '#0a5ef3',
                        '&.Mui-checked': {
                          color: '#0a5ef3',
                        },
                      }}
                    />
                  }
                  label="Booking Access"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: 'Poppins',
                      fontSize: '16px',
                    },
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={handleClosePermissionsModal}
                variant="outlined"
                sx={{
                  borderRadius: '50px',
                  minWidth: '120px',
                  textTransform: 'none',
                  fontFamily: 'Poppins',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  color: '#333',
                  '&:hover': {
                    border: '1px solid #bbb',
                    background: '#f5f5f5',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePermissions}
                variant="contained"
                sx={{
                  borderRadius: '50px',
                  minWidth: '120px',
                  textTransform: 'none',
                  fontFamily: 'Poppins',
                  fontSize: '16px',
                  backgroundColor: '#0a5ef3',
                  '&:hover': {
                    backgroundColor: '#084bc2',
                  },
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          {/* Remove from Organization Confirmation Modal */}
          <Dialog
            open={openRemoveFromOrgModal}
            onClose={handleCloseRemoveFromOrgModal}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '8px',
                padding: '8px 0',
              },
            }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontFamily: 'Poppins',
                fontWeight: '600',
                fontSize: '18px',
                borderBottom: '1px solid #eee',
                pb: 2,
              }}
            >
              Remove from Organization
              <IconButton onClick={handleCloseRemoveFromOrgModal} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '16px',
                  fontWeight: '400',
                  color: '#14171F',
                  lineHeight: '24px',
                }}
              >
                Are you sure you want to remove{' '}
                <strong>
                  {teacherDetails?.first_name} {teacherDetails?.last_name}
                </strong>{' '}
                from {teacherDetails?.org_name || 'Organization'}?
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '14px',
                  fontWeight: '400',
                  color: '#949DA6',
                  lineHeight: '20px',
                  mt: 1,
                }}
              >
                This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={handleCloseRemoveFromOrgModal}
                variant="outlined"
                disabled={isRemovingFromOrg}
                sx={{
                  borderRadius: '50px',
                  minWidth: '120px',
                  textTransform: 'none',
                  fontFamily: 'Poppins',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  color: '#333',
                  '&:hover': {
                    border: '1px solid #bbb',
                    background: '#f5f5f5',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRemoveFromOrganization}
                variant="contained"
                disabled={isRemovingFromOrg}
                sx={{
                  borderRadius: '50px',
                  minWidth: '120px',
                  textTransform: 'none',
                  fontFamily: 'Poppins',
                  fontSize: '16px',
                  backgroundColor: '#E53E3E',
                  '&:hover': {
                    backgroundColor: '#C53030',
                  },
                  '&:disabled': {
                    backgroundColor: '#CBD5E0',
                  },
                }}
              >
                {isRemovingFromOrg ? 'Removing...' : 'Remove'}
              </Button>
            </DialogActions>
          </Dialog>

          {openApproveModal && (
            <TeacherEnableModal
              setOpenApproveModal={setOpenApproveModal}
              rowId={rowId}
              teacherName={teacherName}
              isActive={isActive}
              featureToToggle={featureToToggle}
              isEnabling={isEnabling}
              currentFeatures={{
                canUseWallet,
                canUseChat,
                canUseBookings,
              }}
            />
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default TeacherDetails;
